import axios from "axios";
// Removed unused saveAs import
import debounce from 'lodash/debounce';
import moment from 'moment-timezone';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DropboxChooser from "react-dropbox-chooser";
import useDrivePicker from 'react-google-drive-picker';
import Modal from "react-modal";
import { useDispatch, useSelector } from 'react-redux';
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DatePicker } from "rsuite";
import userCategory from "../../../../src/constants/userCategory";
import userType from "../../../../src/constants/userType";
import { setHeaderText } from "../../../features/headerText/headerTextSlice.js";
import Delete from "../../../images/icons/blue-delete.svg";
import EyeIcon from "../../../images/icons/blue-eye-icon.svg";
import DownloadImage from "../../../images/icons/download-icon.svg";
import Drive from '../../../images/icons/drive.svg';
import Dropbox from '../../../images/icons/dropbox.svg';
import DownArrow from "../../../images/icons/line-down-arrow.svg";
import Device from '../../../images/icons/my-device.svg';
import crssrImage from "../../../images/singleinvoice/cross-red.svg";
import profile_image from "../../../images/user/Default_pfp.png";
import TruncateFilterFieldView from "../../helpers/truncateFilterFieldView.js";
// Removed unused useUserType import
import { useFetchDocumentCategories } from "./DocumentManagement/DocumentCategoryApi.jsx";
import "./GeneralSettings.css";
import { fetchCredentials, fetchUsersService, handleCheckboxAccountTypeChange, handleConfirmDelete, handleDocumentSubmitService, handleViewDocument } from "./services.js";
import { useFetchHrDocuments } from "./useHrDocuments.jsx";
import { HrDocumentsPageSkeleton, ModalSkeleton, ManageCategoriesSkeleton } from "./HrDocumentsTableSkeleton.jsx";

// Lazy load components
const DocumentUploadModal = lazy(() => import("./DocumentUploadModal.jsx"));
const DocumentView = lazy(() => import("./DocumentView.jsx"));
const Table = lazy(() => import("./Table.jsx"));
const Filters = lazy(() => import("./Filters.jsx"));

// Error Boundary Component
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h2>Something went wrong.</h2>
          <p>{this.state.error?.message || "An unexpected error occurred."}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Set the modal app element for accessibility
Modal.setAppElement("#root");

const SUPER_ADMIN = 1;
const ADMIN = 2;

const MemberNameCell = ({ rowData }) => {
  const [showFullname, setShowFullname] = useState(false);

  const fullName = rowData.created_by.first_name
    ? `${rowData.created_by.first_name} ${rowData.created_by.middle_name && rowData.created_by.middle_name !== "null"
      ? rowData.created_by.middle_name + " "
      : ""
    }${rowData.created_by.last_name}`
    : rowData.created_by.company_first_name
      ? `${rowData.created_by.company_first_name} ${rowData.created_by.company_last_name ? rowData.created_by.company_last_name : ""
      }`
      : rowData.created_by.company_name;
  const displayName = showFullname
    ? fullName
    : fullName.length <= 15
      ? fullName
      : fullName.slice(0, 15) + "...";

  return (
    <div
      className={`member-name${showFullname ? " active" : ""}`}
      onMouseEnter={() => {
        if (fullName.length > 15) {
          setShowFullname(true);
        }
      }}
      onMouseLeave={() => setShowFullname(false)}
    >
      {rowData.created_by.profile_image !== null &&
        rowData.created_by.profile_image !== "null" ? (
        <img
          src={`/Uploads/lowProfileImage/${rowData.created_by.profile_image}`}
          alt={fullName}
          className="member-image"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/Uploads/lowProfileImage/user_default.png";
          }}
        />
      ) : (
        <img src={profile_image} alt={fullName} className="member-image" />
      )}
      <span>{displayName}</span>
    </div>
  );
};

export function eyeIcon(rowData, setShowViewModal, setSelectedDocumentData, token) {
  const fileSource = rowData.file?.source || "local";
  const isPdf = rowData.file?.filename?.toLowerCase().endsWith(".pdf");
  if (!isPdf && fileSource !== "gDrive" && fileSource !== "dropbox") {
    return null; // Return null to hide the column content for non-PDFs
  }
  const isExternal = fileSource === "gDrive" || fileSource === "dropbox";
  return (
    <div className="data-table-eye-icon">
      <button
        onClick={(e) => {
          e.preventDefault();
          setSelectedDocumentData(rowData);
          if (isExternal) {
            handleViewDocument(rowData, () => { }, () => { }, () => { }, () => { }, token); // Pass dummy setters and token
          } else {
            setShowViewModal(true);
          }
        }}
        style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
        aria-label="View document"
      >
        <img src={EyeIcon} alt="view-icon" />
      </button>
    </div>
  );
}

export function downArrow(rowData, token) {
  const fileSource = rowData.file?.source || "local";

  if (fileSource === "gDrive" || fileSource === "dropbox") return null;
  const handleDownload = async () => {
    try {
      const response = await axios.get(`/api/hr-documents/download/${rowData._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob", // Handle binary data for download
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", rowData.file.filename); // Use the original filename
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading document:", err);
      toast.error("Failed to download the document. Please try again.", { position: "bottom-right", autoClose: 2000 });
    }
  };

  return (
    <div className="data-table-down-arrow">
      <button
        onClick={(e) => {
          e.preventDefault();
          handleDownload();
        }}
        style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
        aria-label="Download document"
      >
        <img src={DownArrow} alt="down-arrow-icon" />
      </button>
    </div>
  );
}

// Removed unused deleteDoc and handleDownload functions - download is handled in downArrow function

const HrDocuments = (props) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { appSettings } = useSelector((state) => state.appSettings);

  // State definitions
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentCategory, setDocumentCategory] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileAttchFileNameView, setFileAttchFileNameView] = useState('');
  const fileAttchRef = useRef(null);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [filter, setFilter] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [showingDropdownOpen, setShowingDropdownOpen] = useState(false);
  const showingDropdownRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [userCategorySearchTerm, setUserCategorySearchTerm] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [selectAllCategory, setSelectAllCategory] = useState(false);
  const userCategoryDropdownRef = useRef(null);
  const calendarRef = useRef(null);
  const [documentCategorySearchTerm, setDocumentCategorySearchTerm] = useState('');
  const [isDocumentCategoryDropdownOpen, setIsDocumentCategoryDropdownOpen] = useState(false);
  const documentCategoryDropdownRef = useRef(null);
  const accountTypeDropdownRef = useRef(null);
  const [isAccountTypeDropdownOpen, setIsAccountTypeDropdownOpen] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState([]);
  const [accountTypeSearchTerm, setAccountTypeSearchTerm] = useState('');
  const [selectAllAccountType, setSelectAllAccountType] = useState(false);
  const [hasUserSelectedAccountType, setHasUserSelectedAccountType] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDocumentData, setSelectedDocumentData] = useState(null);
  const [selectedDocumentUrl, setSelectedDocumentUrl] = useState('');
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [error, setError] = useState(null);
  // Removed unused modal-related state variables: selectedAccountTypeModal, selectAllAccountTypeModal,
  // isAccountTypeModalDropdownOpen, roleIdSearchTerm, accountTypeModalDropdownRef
  const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);
  const [selectedTeamUsers, setSelectedTeamUsers] = useState([]);
  const [selectAllTeamUsers, setSelectAllTeamUsers] = useState(false);
  const teamDropdownRef = useRef(null);
  const [isVendorDropdownOpen, setIsVendorDropdownOpen] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectAllVendors, setSelectAllVendors] = useState(false);
  const vendorDropdownRef = useRef(null);
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectAllClients, setSelectAllClients] = useState(false);
  const clientDropdownRef = useRef(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [clients, setClients] = useState([]);
  const [isUploadSourceModalOpen, setIsUploadSourceModalOpen] = useState(false);
  const [googleDriveClientId, setGoogleDriveClientId] = useState(null);
  const [googleDriveDeveloperKey, setGoogleDriveDeveloperKey] = useState(null);
  const [dropBoxClientId, setDropBoxClientId] = useState(null);
  const [cloudFileData, setCloudFileData] = useState(null);

  const token = useSelector((state) => state.auth.user.token);
  const [openPicker] = useDrivePicker(); // Removed unused 'data' variable

  // Move useFetchDocumentCategories before useMemo to ensure initialization
  const { data: documentCategories = [] } = useFetchDocumentCategories(token);

  // Fetch documents using React Query
  const {
    data: documentsData = { documents: [], total: 0 },
    isLoading: loadingDocuments,
    isFetching: isFetchingDocuments,
    error: documentsError,
    refetch: refetchDocuments,
  } = useFetchHrDocuments({
    token: user?.token,
    page: currentPage,
    limit: perPage,
    search: filter,
  });

  const documents = useMemo(() => documentsData.documents || [], [documentsData.documents]);
  const totalRecords = documentsData.total || 0;

  // Memoized derived data with guard clauses
  const usedCategoryIds = useMemo(() => new Set(documents.map(doc => doc.category?._id)), [documents]);
  const filteredDocumentCategories = useMemo(
    () => {
      if (!documentCategories) return []; // Guard against undefined documentCategories
      return documentCategories.data?.filter(cat => usedCategoryIds.has(cat._id)) || [];
    },
    [documentCategories, usedCategoryIds]
  );
  const userCategoryList = useMemo(() => [
    {
      name: 'My Team',
      value: [userCategory.Individual, userCategory.Business],
    },
    {
      name: 'Vendor',
      value: [userCategory.Vendor],
    },
    {
      name: 'Client',
      value: [userCategory.Client],
    }
  ], []);
  const filteredUserCategoryList = useMemo(
    () => userCategoryList.filter((category) =>
      category.name.toLowerCase().includes(userCategorySearchTerm?.toLowerCase())
    ),
    [userCategoryList, userCategorySearchTerm]
  );
  const selectedNames = useMemo(
    () => userCategoryList
      .filter((category) => category.value.every((val) => selectedCategory.includes(val)))
      .map((c) => c.name),
    [userCategoryList, selectedCategory]
  );

  const displayLabel = useMemo(
    () =>
      selectedNames.length === 0
        ? 'Select type of users'
        : selectedNames.length === userCategoryList.length
          ? 'All'
          : `${selectedNames[0]}${selectedNames.length > 1
            ? `<div class="selected-option-count">+${selectedNames.length - 1}</div>`
            : ''
          }`,
    [selectedNames, userCategoryList]
  );

  // Debounced filter change handler
  const debouncedHandleFilterChange = useMemo(
    () => debounce((value) => {
      setFilter(value);
      setCurrentPage(0);
    }, 300),
    [] // debounce function is stable, setters are stable
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedHandleFilterChange.cancel();
    };
  }, [debouncedHandleFilterChange]);

  // Combined useEffect for initialization
  useEffect(() => {
    dispatch(setHeaderText("Documents"));

    if (appSettings?.appId && user?.token) {
      fetchCredentials(appSettings, user, setGoogleDriveClientId, setGoogleDriveDeveloperKey, setDropBoxClientId, toast);
    }
  }, [user, appSettings, dispatch]);

  // Removed unused roleId - was only used for filteredRoleId which is also unused

  const filteredDocuments = useMemo(() => {
    let filtered = [...documents];

    if (selectedDate) {
      const selected = moment(selectedDate).startOf("day");
      filtered = filtered.filter((item) =>
        moment(item.created_at).isSame(selected, "day")
      );
    }

    filtered = filtered.filter((item) =>
      selectedAccountType.includes(item.category?._id?.toString())
    );

    return filtered;
  }, [documents, selectedDate, selectedAccountType]);

  // Set error state if query fails
  useEffect(() => {
    if (documentsError) {
      setError(documentsError.message || 'Failed to load documents.');
    }
  }, [documentsError]); // setError is stable, doesn't need to be in dependencies


  // Handle filter change for search
  const handleFilterChange = (e) => {
    setInputValue(e.target.value);
    debouncedHandleFilterChange(e.target.value);
  };

  // Handle page change for DataTable pagination
  const handlePageChange = (event) => {
    setCurrentPage(event.page);
  };

  // Handle showing dropdown change
  const handleShowingChange = (value) => {
    setPerPage(value);
    setShowingDropdownOpen(false);
    setCurrentPage(0);
  };

  // Handle click outside to close dropdowns
  const handleClickOutsideDropdown = (event) => {
    if (
      showingDropdownRef.current && !showingDropdownRef.current.contains(event.target)
    ) {
      setShowingDropdownOpen(false);
    }
    if (
      accountTypeDropdownRef.current && !accountTypeDropdownRef.current.contains(event.target)
    ) {
      setIsAccountTypeDropdownOpen(false);
    }
    if (
      documentCategoryDropdownRef.current && !documentCategoryDropdownRef.current.contains(event.target)
    ) {
      setIsDocumentCategoryDropdownOpen(false);
    }
    if (
      userCategoryDropdownRef.current && !userCategoryDropdownRef.current.contains(event.target)
    ) {
      setIsCategoryDropdownOpen(false);
    }
    // Removed accountTypeModalDropdownRef check - modal functionality not used
    if (teamDropdownRef.current && !teamDropdownRef.current.contains(event.target)) {
      setIsTeamDropdownOpen(false);
    }
    if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
      setIsVendorDropdownOpen(false);
    }
    if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
      setIsClientDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideDropdown);
    return () => {
      document.removeEventListener('click', handleClickOutsideDropdown);
    };
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      setFileAttchFileNameView(file.name);
      setCloudFileData(null);
      setIsUploadSourceModalOpen(false);
    } else {
      setUploadedFile(null);
      setFileAttchFileNameView('');
      setCloudFileData(null);
    }
  };

  // Removed unused handleOpenUploadSourceModal function

  const handleCloseUploadSourceModal = () => {
    setIsUploadSourceModalOpen(false);
  };

  const handleMyDeviceClick = () => {
    fileAttchRef.current.click();
  };

  const handleGoogleDrivePicker = () => {
    if (googleDriveClientId && googleDriveDeveloperKey) {
      openPicker({
        clientId: googleDriveClientId.trim(),
        developerKey: googleDriveDeveloperKey.trim(),
        viewId: 'DOCS',
        showUploadView: true,
        showUploadFolders: true,
        supportDrives: true,
        multiselect: false,
        mimeType: '*/*',
        callbackFunction: (data) => {
          if (data.action === 'picked') {
            const fileData = {
              name: data.docs[0].name,
              url: data.docs[0].url,
              embedUrl: data.docs[0].embedUrl,
              iconUrl: data.docs[0].iconUrl,
              type: data.docs[0].type,
              source: 'gDrive',
            };
            setCloudFileData(fileData);
            setFileAttchFileNameView(data.docs[0].name);
            setUploadedFile(null);
            setIsUploadSourceModalOpen(false);
          } else if (data.action === 'cancel') {
            console.log('Google Drive picker cancelled');
          }
        },
      });
    } else {
      toast.error('Google Drive configuration is missing.', {
        position: 'bottom-right',
        autoClose: 2000,
      });
    }
  };

  const handleDropboxSuccess = (files) => {
    if (files.length > 0) {
      const fileData = {
        name: files[0].name,
        url: files[0].link,
        embedUrl: files[0].link,
        iconUrl: files[0].icon,
        size: files[0].bytes,
        source: 'dropbox',
      };
      setCloudFileData(fileData);
      setFileAttchFileNameView(files[0].name);
      setUploadedFile(null);
      setIsUploadSourceModalOpen(false);
    }
  };

  // Handle document modal close
  const handleDocumentClose = () => {
    setShowDocumentModal(false);
    setDocumentCategory("");
    setDocumentTitle("");
    setSelectedCategory([]);
    setSelectedTeamUsers([]);
    setSelectedVendors([]);
    setSelectedClients([]);
    setSelectAllTeamUsers(false);
    setSelectAllVendors(false);
    setSelectAllClients(false);
    setSelectAllCategory(false);
    setUploadedFile(null);
    setCloudFileData(null);
    setFileAttchFileNameView('');
  };

  // Handle adding a new document
  const handleDocumentSubmit = useCallback(async (e) => {
    e.preventDefault();

    try {
      await handleDocumentSubmitService(documentCategories?.data || [], documentCategory, documentTitle, selectedCategory, selectedTeamUsers, selectedVendors, selectedClients, userCategory, toast, uploadedFile, cloudFileData, setIsSubmitLoading, user, refetchDocuments, currentPage, perPage, handleDocumentClose);

    } catch (error) {
      console.error("Error adding document:", error);
      toast.error(error.message || "Failed to add document. Please try again.", { position: "bottom-right", autoClose: 2000 });
    } finally {
      setIsSubmitLoading(false);
    }
  }, [documentCategory, documentTitle, selectedCategory, selectedTeamUsers, selectedVendors, selectedClients, uploadedFile, cloudFileData, user, refetchDocuments, currentPage, perPage, documentCategories]);


  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDocumentToDelete(null);
    document.body.classList.remove("ReactModal__Body--open");
  };

  // Render delete button for the Actions column
  const renderDeleteButton = (rowData) => {
    return (
      <button
        onClick={() => {
          setDocumentToDelete(rowData);
          setShowDeleteModal(true);
        }}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
        aria-label="Delete document"
      >
        <span className="icon-invoice">
          <img src={Delete} alt="Delete" />
        </span>
      </button>
    );
  };

  const handleToggleDocumentCategoryDropdown = () => {
    setIsDocumentCategoryDropdownOpen(!isDocumentCategoryDropdownOpen);
    setDocumentCategorySearchTerm('');
  };

  const handleDocumentCategorySearchChange = (e) => {
    setDocumentCategorySearchTerm(e.target.value);
  };

  const toggleCategoryDropdown = () => {
    setUserCategorySearchTerm("");
    setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
  };

  // Removed unused filteredRoleId and related functions - they were for modal functionality that's not used

  const handleSelectAllCategoryChange = () => {
    setSelectAllCategory(!selectAllCategory);
    if (!selectAllCategory) {
      const allValues = userCategoryList.flatMap((item) => item.value);
      setSelectedCategory(allValues);
    } else {
      setSelectedCategory([]);
      setSelectedTeamUsers([]);
      setSelectAllTeamUsers(false);
      setSelectedVendors([]);
      setSelectAllVendors(false);
      setSelectedClients([]);
      setSelectAllClients(false);
    }
  };

  const handleCheckboxCategoryChange = (values) => {
    setSelectedCategory((prevSelected) => {
      let newSelected;

      const allSelected = values.every((val) => prevSelected.includes(val));

      if (allSelected) {
        newSelected = prevSelected.filter((val) => !values.includes(val));
      } else {
        const merged = [...prevSelected];
        values.forEach((val) => {
          if (!merged.includes(val)) merged.push(val);
        });
        newSelected = merged;
      }

      if (!(newSelected.includes(userCategory.Individual) && newSelected.includes(userCategory.Business))) {
        setSelectedTeamUsers([]);
        setSelectAllTeamUsers(false);
      }
      if (!newSelected.includes(userCategory.Vendor)) {
        setSelectedVendors([]);
        setSelectAllVendors(false);
      }
      if (!newSelected.includes(userCategory.Client)) {
        setSelectedClients([]);
        setSelectAllClients(false);
      }

      const allCategoryValues = userCategoryList.flatMap((item) => item.value);
      const isAllSelected = allCategoryValues.every((val) => newSelected.includes(val));
      setSelectAllCategory(isAllSelected);

      return newSelected;
    });
  };

  useEffect(() => {
    if (!hasUserSelectedAccountType && filteredDocumentCategories?.length > 0) {
      const allCategoryIds = filteredDocumentCategories.map((cat) => cat._id);
      setSelectedAccountType(allCategoryIds);
      setSelectAllAccountType(true);
    }
  }, [filteredDocumentCategories, hasUserSelectedAccountType]);

  const toggleAccountTypeDropdown = () => {
    setAccountTypeSearchTerm('');
    setIsAccountTypeDropdownOpen(!isAccountTypeDropdownOpen);
  };

  const handleAccountTypeSearchChange = (e) => {
    setAccountTypeSearchTerm(e.target.value);
  };

  const handleSelectAllAccountTypeChange = () => {
    setHasUserSelectedAccountType(true);

    const newValue = !selectAllAccountType;
    setSelectAllAccountType(newValue);

    if (newValue) {
      setSelectedAccountType(filteredDocumentCategories.map((cat) => cat._id));
    } else {
      setSelectedAccountType([]);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        await fetchUsersService(selectedCategory, user, setTeamUsers, setVendors, setClients, userCategory, toast);
      } catch (error) {
        console.error('Error fetching users:', error);
        // toast.error('Failed to load users.', { position: 'bottom-right', autoClose: 2000 });
      }
    };

    if (selectedCategory.length > 0) {
      fetchUsers();
    } else {
      setTeamUsers([]);
      setVendors([]);
      setClients([]);
    }
  }, [selectedCategory, user]);

  // Toggle dropdowns
  const toggleTeamDropdown = () => {
    setIsTeamDropdownOpen(!isTeamDropdownOpen);
  };

  const toggleVendorDropdown = () => {
    setIsVendorDropdownOpen(!isVendorDropdownOpen);
  };

  const toggleClientDropdown = () => {
    setIsClientDropdownOpen(!isClientDropdownOpen);
  };

  // Handle individual user selections
  const handleCheckboxTeamUsersChange = (userId) => {
    setSelectedTeamUsers((prev) => {
      const newSelected = prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId];
      setSelectAllTeamUsers(teamUsers.length > 0 && newSelected.length === teamUsers.length);
      return newSelected;
    });
  };

  const handleCheckboxVendorsChange = (vendorId) => {
    setSelectedVendors((prev) => {
      const newSelected = prev.includes(vendorId)
        ? prev.filter((id) => id !== vendorId)
        : [...prev, vendorId];
      setSelectAllVendors(vendors.length > 0 && newSelected.length === vendors.length);
      return newSelected;
    });
  };

  const handleCheckboxClientsChange = (clientId) => {
    setSelectedClients((prev) => {
      const newSelected = prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId];
      setSelectAllClients(clients.length > 0 && newSelected.length === clients.length);
      return newSelected;
    });
  };

  // Handle select all
  const handleSelectAllTeamUsersChange = () => {
    setSelectAllTeamUsers(!selectAllTeamUsers);
    if (!selectAllTeamUsers) {
      setSelectedTeamUsers(teamUsers.map((user) => user._id));
    } else {
      setSelectedTeamUsers([]);
    }
  };

  const handleSelectAllVendorsChange = () => {
    setSelectAllVendors(!selectAllVendors);
    if (!selectAllVendors) {
      setSelectedVendors(vendors.map((vendor) => vendor._id));
    } else {
      setSelectedVendors([]);
    }
  };

  const handleSelectAllClientsChange = () => {
    setSelectAllClients(!selectAllClients);
    if (!selectAllClients) {
      setSelectedClients(clients.map((client) => client._id));
    } else {
      setSelectedClients([]);
    }
  };

  // Handle role-based team user selection
  const handleSelectTeamUsersByRole = (roleId) => {
    const filteredUsers = teamUsers
      .filter((user) => user.role_id === roleId)
      .map((user) => user._id);
    setSelectedTeamUsers((prev) => {
      let newSelected;
      if (filteredUsers.every((id) => prev.includes(id))) {
        newSelected = prev.filter((id) => !filteredUsers.includes(id));
      } else {
        newSelected = [...new Set([...prev, ...filteredUsers])];
      }
      setSelectAllTeamUsers(teamUsers.length > 0 && newSelected.length === teamUsers.length);
      return newSelected;
    });
  };

  const timeZone = global?.config?.getTimeZone();
  const ranges = [
    {
      label: "Today",
      value: moment.tz(timeZone).startOf("day").toDate(),
    },
  ];

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  // Removed unused reverseUserCategory variable

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedDocumentUrl('');
    setError(null);
    setLoadingDocument(false);
  };

  // Fetch document when modal opens
  useEffect(() => {
    const fetchDocument = async () => {
      const file = selectedDocumentData?.file;
      const fileSource = file?.source || "local";
      if (showViewModal && !fileSource.match(/gDrive|dropbox/) && selectedDocumentData) {
        await handleViewDocument(
          selectedDocumentData,
          setLoadingDocument,
          setError,
          setShowViewModal,
          setSelectedDocumentUrl,
          token
        );
      }
    };

    fetchDocument();
  }, [showViewModal, selectedDocumentData, token]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<HrDocumentsPageSkeleton />}>
        <div className="user-outer-overview invoice-outer-invoice invite-history document-hr-sec-outer">
          <div className="user-outer-overview add-bank-account">
            {!user || !user.userType ? (
              <ManageCategoriesSkeleton />
            ) : (user.userType === SUPER_ADMIN || user.userType === ADMIN) ? (
              <div className="manage manage-time-off">
                <Link to={`/user/documents/categories`}>
                  <span className="icon-invoice">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_1143_332)">
                        <path d="M19.0704 7.87245C18.7124 7.87245 18.3983 7.6613 18.2626 7.33065L18.2304 7.25449C18.0947 6.92941 18.1709 6.55604 18.4255 6.30093C19.1789 5.54799 19.1733 4.32322 18.4255 3.57585L17.4281 2.57895C16.702 1.85263 15.4444 1.84768 14.7121 2.5678L14.7009 2.57895C14.4463 2.82786 14.0721 2.90402 13.7525 2.76842L13.6763 2.73622C13.3454 2.60619 13.1342 2.28607 13.1342 1.92879C13.1342 0.866873 12.2668 0 11.2044 0H9.78936C8.72688 0 7.85954 0.866873 7.85954 1.92879C7.85954 2.28669 7.64828 2.60619 7.32303 2.73622L7.25241 2.76842C6.92716 2.90402 6.55359 2.83344 6.29834 2.57895C5.545 1.83158 4.32516 1.83158 3.57182 2.57895L2.57438 3.57585C1.82104 4.32879 1.82104 5.54799 2.57438 6.3065C2.82901 6.56099 2.89963 6.92941 2.76396 7.26006L2.7367 7.33065C2.6066 7.6613 2.28631 7.87245 1.92884 7.87245C0.866357 7.87245 -0.000976562 8.73932 -0.000976562 9.80124V11.2099C-0.000976562 12.2774 0.866357 13.1387 1.91831 13.1387C2.27639 13.1387 2.59049 13.3498 2.72617 13.6749L2.75838 13.7511C2.89406 14.0762 2.81786 14.4495 2.56323 14.7046C1.81485 15.4576 1.81485 16.6824 2.56881 17.4297L3.57182 18.4266C4.32516 19.1746 5.545 19.1746 6.29834 18.4211C6.54739 18.1721 6.92158 18.096 7.24683 18.2316L7.31746 18.2638C7.64828 18.3938 7.85954 18.7139 7.85954 19.0712C7.85954 20.1331 8.72688 21 9.78936 21H11.2044C12.2668 21 13.1342 20.1387 13.1391 19.0656C13.1391 18.7077 13.3504 18.3938 13.6812 18.2582C13.6868 18.2582 13.7518 18.231 13.7518 18.231C14.0771 18.0954 14.4513 18.1715 14.7003 18.4204L14.7115 18.4316C15.4376 19.1523 16.7008 19.1467 17.4275 18.4204L18.4249 17.4235C19.1782 16.6762 19.1782 15.457 18.4249 14.6985C18.1759 14.4495 18.0996 14.0755 18.2353 13.7505L18.2626 13.6799C18.3983 13.3492 18.713 13.1381 19.0704 13.1381C20.1329 13.1381 21.0003 12.2712 21.0003 11.2093V9.80062C21.0003 8.7387 20.1329 7.87183 19.0704 7.87183V7.87245ZM19.965 11.2037C19.965 11.6861 19.5692 12.0817 19.0866 12.0817C18.2948 12.0817 17.596 12.5529 17.3029 13.2793L17.2757 13.3443C16.9721 14.065 17.1351 14.8885 17.6932 15.4464C18.0346 15.7932 18.0346 16.3461 17.6932 16.6873L16.6958 17.6842C16.3538 18.0254 15.7956 18.0254 15.4543 17.6842L15.4431 17.6731C14.8905 17.1257 14.0666 16.9684 13.351 17.2669L13.5517 17.7492L13.286 17.2885C12.5599 17.5814 12.0878 18.2805 12.0878 19.0712C12.0878 19.5536 11.6919 19.9492 11.2093 19.9492H9.79431C9.31171 19.9492 8.91583 19.5591 8.91583 19.0712C8.91583 18.2854 8.44995 17.587 7.72325 17.2941L7.6582 17.2669C7.41968 17.1641 7.16506 17.1152 6.91539 17.1152C6.41667 17.1152 5.92911 17.3158 5.57102 17.6842C5.22904 18.0254 4.67085 18.0254 4.32949 17.6842L3.33206 16.6873C2.99008 16.3461 2.99008 15.7882 3.33764 15.4415C3.89025 14.8892 4.05319 14.065 3.74962 13.3449L3.72236 13.2799C3.42437 12.5591 2.72493 12.0879 1.93875 12.0879C1.45614 12.0879 1.06027 11.6923 1.06027 11.2099V9.80124C1.06027 9.31889 1.45057 8.92322 1.93875 8.92322C2.72493 8.92322 3.42437 8.45201 3.72236 7.7257L3.74962 7.66625C4.05319 6.94551 3.89025 6.12198 3.33206 5.56409C2.9907 5.21734 2.9907 4.6644 3.33206 4.32322L4.32949 3.32632C4.67147 2.98514 5.22966 2.98514 5.57102 3.32632C6.12426 3.88421 6.9476 4.04706 7.6743 3.74365L7.73378 3.72198C8.4549 3.42415 8.92636 2.72508 8.92636 1.93932C8.92636 1.45697 9.32224 1.0613 9.80485 1.0613H11.2198C11.7024 1.0613 12.0983 1.45139 12.0983 1.93932C12.0983 2.72508 12.5642 3.42353 13.2909 3.71641L13.5077 3.23406L13.356 3.73808C14.0771 4.03591 14.8955 3.87864 15.4481 3.33189L15.4592 3.32074C15.8012 2.97957 16.3594 2.97957 16.7008 3.32074L17.6982 4.31765C18.0402 4.65944 18.0402 5.21176 17.6926 5.55851C17.1394 6.11641 16.9771 6.93498 17.2751 7.65511L17.3023 7.72012C17.6003 8.44582 18.2998 8.91765 19.0859 8.91765C19.5685 8.91765 19.9644 9.31331 19.9644 9.79567V11.2043L19.965 11.2037Z" fill="#6A7988" />
                        <path d="M10.4949 5.60248C7.79011 5.60248 5.58398 7.80743 5.58398 10.5108C5.58398 13.2142 7.78453 15.4192 10.4949 15.4192C13.2054 15.4192 15.4065 13.2142 15.4059 10.5108C15.4059 7.80743 13.1998 5.60248 10.4949 5.60248ZM10.4949 14.3635C8.36998 14.3635 6.63532 12.6297 6.63532 10.5059C6.63532 8.38204 8.36441 6.6483 10.4949 6.6483C12.6255 6.6483 14.3546 8.38204 14.3546 10.5059C14.3546 12.6297 12.6199 14.3635 10.4949 14.3635Z" fill="#6A7988" />
                      </g>
                      <defs>
                        <clipPath id="clip0_1143_332">
                          <rect width="21" height="21" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </span>
                  Manage Categories
                </Link>
              </div>
            ) : null}
            <div className="invoice-top">
              <div className="button-sec-invoice upload-doc-hr">
                {(user.userType === SUPER_ADMIN || user.userType === ADMIN) &&
                  <button onClick={() => setShowDocumentModal(true)}>
                    <span className="icon-invoice">
                      <img src={DownloadImage} alt="Export" />
                    </span>
                    Upload Document
                  </button>
                }
              </div>
              <div className="search-sec">
                <div className="search-container">
                  <input
                    type="text"
                    onChange={handleFilterChange}
                    value={inputValue}
                    className="search-input"
                    placeholder={global.config.locate(appSettings && appSettings.language, 'Search') || "Search"}
                  />
                </div>
              </div>
            </div>

            <Filters
              selectedAccountType={selectedAccountType}
              filteredDocumentCategories={filteredDocumentCategories}
              allDocumentCategories={documentCategories?.data || []}
              isAccountTypeDropdownOpen={isAccountTypeDropdownOpen}
              accountTypeDropdownRef={accountTypeDropdownRef}
              toggleAccountTypeDropdown={toggleAccountTypeDropdown}
              handleAccountTypeSearchChange={handleAccountTypeSearchChange}
              accountTypeSearchTerm={accountTypeSearchTerm}
              selectAllAccountType={selectAllAccountType}
              handleSelectAllAccountTypeChange={handleSelectAllAccountTypeChange}
              handleCheckboxAccountTypeChange={handleCheckboxAccountTypeChange}
              calendarRef={calendarRef}
              selectedDate={selectedDate}
              handleDateChange={handleDateChange}
              ranges={ranges}
              appSettings={appSettings}
              totalRecords={totalRecords}
              perPage={perPage}
              showingDropdownOpen={showingDropdownOpen}
              setShowingDropdownOpen={setShowingDropdownOpen}
              showingDropdownRef={showingDropdownRef}
              handleShowingChange={handleShowingChange}
              TruncateFilterFieldView={TruncateFilterFieldView}
              DatePicker={DatePicker}
              setSelectedAccountType={setSelectedAccountType}
              setSelectAllAccountType={setSelectAllAccountType}
              isFetchingDocuments={isFetchingDocuments}
            />

            <div className="card-outer1">
              <div className="card-outer card-outer-account">
                <div className="card">
                  <Table
                    DataTable={DataTable}
                    Column={Column}
                    filteredDocuments={filteredDocuments}
                    loadingDocuments={loadingDocuments || isFetchingDocuments}
                    currentPage={currentPage}
                    perPage={perPage}
                    totalRecords={totalRecords}
                    handlePageChange={handlePageChange}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    setSortField={setSortField}
                    setSortOrder={setSortOrder}
                    setCurrentPage={setCurrentPage}
                    downArrow={downArrow}
                    eyeIcon={eyeIcon}
                    setShowViewModal={setShowViewModal}
                    setSelectedDocumentData={setSelectedDocumentData}
                    renderMemberName={(rowData) => <MemberNameCell rowData={rowData} />}
                    renderDeleteButton={renderDeleteButton}
                    moment={moment}
                    token={token}
                    user={user}
                    SUPER_ADMIN={SUPER_ADMIN}
                    ADMIN={ADMIN}
                  />
                </div>
              </div>
            </div>

            <Modal
              className="add-doc-modal"
              isOpen={showDocumentModal}
              onRequestClose={!isSubmitLoading ? handleDocumentClose : undefined}
              contentLabel="Create Document"
              style={{
                content: {
                  height: "41%",
                  width: "30%",
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  parent: document.querySelector(".admin-outer.time.tracker"),
                },
              }}
            >
              <Suspense fallback={<ModalSkeleton />}>
                <DocumentUploadModal
                  handleDocumentClose={handleDocumentClose}
                  crssrImage={crssrImage}
                  handleDocumentSubmit={handleDocumentSubmit}
                  isSubmitLoading={isSubmitLoading}
                  handleToggleDocumentCategoryDropdown={handleToggleDocumentCategoryDropdown}
                  isDocumentCategoryDropdownOpen={isDocumentCategoryDropdownOpen}
                  documentCategoryDropdownRef={documentCategoryDropdownRef}
                  documentCategory={documentCategory}
                  documentCategories={documentCategories?.data || []}
                  setDocumentCategory={setDocumentCategory}
                  handleDocumentCategorySearchChange={handleDocumentCategorySearchChange}
                  documentCategorySearchTerm={documentCategorySearchTerm}
                  documentTitle={documentTitle}
                  setDocumentTitle={setDocumentTitle}
                  isCategoryDropdownOpen={isCategoryDropdownOpen}
                  userCategoryDropdownRef={userCategoryDropdownRef}
                  toggleCategoryDropdown={toggleCategoryDropdown}
                  displayLabel={displayLabel}
                  filteredUserCategoryList={filteredUserCategoryList}
                  userCategorySearchTerm={userCategorySearchTerm}
                  handleSelectAllCategoryChange={handleSelectAllCategoryChange}
                  selectedCategory={selectedCategory}
                  userCategory={userCategory}
                  handleCheckboxCategoryChange={handleCheckboxCategoryChange}
                  isTeamDropdownOpen={isTeamDropdownOpen}
                  teamDropdownRef={teamDropdownRef}
                  toggleTeamDropdown={toggleTeamDropdown}
                  teamUsers={teamUsers}
                  selectedTeamUsers={selectedTeamUsers}
                  isVendorDropdownOpen={isVendorDropdownOpen}
                  vendorDropdownRef={vendorDropdownRef}
                  toggleVendorDropdown={toggleVendorDropdown}
                  vendors={vendors}
                  selectedVendors={selectedVendors}
                  isClientDropdownOpen={isClientDropdownOpen}
                  clientDropdownRef={clientDropdownRef}
                  toggleClientDropdown={toggleClientDropdown}
                  clients={clients}
                  selectedClients={selectedClients}
                  selectAllCategory={selectAllCategory}
                  selectAllTeamUsers={selectAllTeamUsers}
                  selectAllVendors={selectAllVendors}
                  selectAllClients={selectAllClients}
                  handleSelectAllTeamUsersChange={handleSelectAllTeamUsersChange}
                  handleSelectTeamUsersByRole={handleSelectTeamUsersByRole}
                  handleCheckboxTeamUsersChange={handleCheckboxTeamUsersChange}
                  handleSelectAllVendorsChange={handleSelectAllVendorsChange}
                  handleCheckboxVendorsChange={handleCheckboxVendorsChange}
                  handleSelectAllClientsChange={handleSelectAllClientsChange}
                  handleCheckboxClientsChange={handleCheckboxClientsChange}
                  fileAttchRef={fileAttchRef}
                  handleFileUpload={handleFileUpload}
                  handleMyDeviceClick={handleMyDeviceClick}
                  handleGoogleDrivePicker={handleGoogleDrivePicker}
                  googleDriveClientId={googleDriveClientId}
                  googleDriveDeveloperKey={googleDriveDeveloperKey}
                  dropBoxClientId={dropBoxClientId}
                  handleDropboxSuccess={handleDropboxSuccess}
                  fileAttchFileNameView={fileAttchFileNameView}
                  userType={userType}
                  Device={Device}
                  Drive={Drive}
                  Dropbox={Dropbox}
                  DropboxChooser={DropboxChooser}
                  setIsDocumentCategoryDropdownOpen={setIsDocumentCategoryDropdownOpen}
                />
              </Suspense>
            </Modal>

            <Modal
              className="document-upload-modal"
              isOpen={isUploadSourceModalOpen}
              onRequestClose={!isSubmitLoading ? handleCloseUploadSourceModal : undefined}
              contentLabel="Choose Upload Source"
              style={{
                content: {
                  height: '41%',
                  width: '30%',
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: "translate(-50%, -50%)",
                  parent: document.querySelector('.admin-outer.time.tracker'),
                },
              }}
            >
              <Suspense fallback={<ModalSkeleton />}>
                <div className="modal timeoff-modal">
                  <div className="modal-top-sec-with-close">
                    <div className="modal-client-head">
                      <h2 className="">Upload Document</h2>
                    </div>
                    <div className="modal-close-invite outter-modal-main">
                      <button className="" onClick={handleCloseUploadSourceModal} disabled={isSubmitLoading} style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                        <img src={crssrImage} alt="Close Icon" />
                      </button>
                    </div>
                  </div>
                  <div className="form-field-client-outer">
                    <div className="subtitle-document">Please choose a service to connect</div>
                    <div className="document-upload-outer">
                      <div className="document-upload-inner">
                        <div className="document-upload-box" onClick={handleMyDeviceClick}>
                          <div className="icon-upload">
                            <img src={Device} alt="My Device" />
                          </div>
                          <div className="upload-name">
                            {isSubmitLoading ? 'Loading...' : 'My Device'}
                          </div>
                        </div>

                        {googleDriveClientId && googleDriveDeveloperKey && (
                          <div className="document-upload-box" onClick={handleGoogleDrivePicker}>
                            <div className="icon-upload">
                              <img src={Drive} alt="Google Drive" />
                            </div>
                            <div className="upload-name">Drive</div>
                          </div>
                        )}

                        {dropBoxClientId && (
                          <DropboxChooser
                            appKey={dropBoxClientId.trim()}
                            success={handleDropboxSuccess}
                            cancel={() => console.log('Dropbox picker cancelled')}
                          >
                            <div className="document-upload-box">
                              <div className="icon-upload">
                                <img src={Dropbox} alt="Dropbox" />
                              </div>
                              <div className="upload-name">Dropbox</div>
                            </div>
                          </DropboxChooser>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Suspense>
            </Modal>

            <Modal
              className="delete-modal"
              isOpen={showDeleteModal}
              onRequestClose={handleCancelDelete}
              contentLabel="Confirm Delete"
              style={{
                content: {
                  height: "41%",
                  width: "30%",
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  parent: document.querySelector(".admin-outer.time.tracker"),
                },
              }}
            >
              <Suspense fallback={<ModalSkeleton />}>
                <div className="delete-modal-content">
                  <div className="outter-modal-main">
                    <button className="close-image-clr1" onClick={handleCancelDelete} style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                      <img src={crssrImage} alt="Close Icon" />
                    </button>
                  </div>
                  <h2>Confirm Delete</h2>
                  <p>Are you sure you want to delete the document "{documentToDelete?.title}"?</p>
                  <div className="delete-modal-buttons">
                    <button className="buttons-rejected"
                      onClick={() => handleConfirmDelete(documentToDelete, user, currentPage, perPage, setIsSubmitLoading, toast, refetchDocuments, handleCancelDelete)} disabled={isSubmitLoading}

                    >
                      {isSubmitLoading ? "Deleting..." : "Delete"}
                    </button>
                    <button onClick={handleCancelDelete} disabled={isSubmitLoading}>
                      Cancel
                    </button>
                  </div>
                </div>
              </Suspense>
            </Modal>

            <Modal
              className="document-upoload-modal hr-doc-modal-sec-inner"
              isOpen={showViewModal}
              onRequestClose={handleCloseViewModal}
              contentLabel="View Document"
              style={{
                content: {
                  width: "80%",
                  maxWidth: "1100px",
                  height: "85vh",
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  padding: 0,
                  border: "none",
                  borderRadius: "12px",
                  overflow: "hidden",
                },
              }}
            >
              <Suspense fallback={<ModalSkeleton />}>
                <DocumentView
                  handleCloseViewModal={handleCloseViewModal}
                  selectedDocumentUrl={selectedDocumentUrl}
                  loadingDocument={loadingDocument}
                  crssrImage={crssrImage}
                  error={error}
                />
              </Suspense>
            </Modal>
          </div>
        </div>
      </Suspense>
      <ToastContainer />
    </ErrorBoundary>
  );
};

export default HrDocuments;